import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, QrCode, Receipt, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, brl, errMsg, fmtDate } from "@/lib/api";
import { PERIOD_LABELS } from "@/lib/boy";
import { cn } from "@/lib/utils";

const endDay = (iso) => fmtDate(new Date(new Date(iso).getTime() - 86400000).toISOString());

function PixDialog({ inv, onClose, onPaid }) {
  const [busy, setBusy] = useState(false);
  if (!inv) return null;
  const ch = inv.charge || {};
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ch.qr_code);
      toast.success("Código Pix copiado");
    } catch {
      toast.error("Copie o código manualmente");
    }
  };
  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/boy/fees/${inv.id}`);
      if (data.status === "paga") { toast.success("Pagamento confirmado. Obrigado!"); onPaid(); } else toast.message("Ainda não recebemos o pagamento. Tente de novo em instantes.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="boy-fee-pix-dialog">
        <DialogHeader><DialogTitle>Pagar {brl(inv.amount)} com Pix</DialogTitle></DialogHeader>
        {ch.qr_code_base64 && <img src={`data:image/png;base64,${ch.qr_code_base64}`} alt="QR Code Pix" className="mx-auto h-56 w-56" />}
        <Button variant="outline" onClick={copy} data-testid="boy-fee-pix-copy"><Copy className="mr-2 h-4 w-4" />Copiar Pix copia e cola</Button>
        <Button onClick={check} disabled={busy} data-testid="boy-fee-pix-check">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Já paguei, conferir</Button>
      </DialogContent>
    </Dialog>
  );
}

const Box = ({ title, value, hint, testId }) => <div className="card-surface p-4" data-testid={testId}><p className="eyebrow">{title}</p><p className="mt-1 text-2xl font-bold">{value}</p><p className="text-xs text-muted-foreground">{hint}</p></div>;

export function FeesTab({ mgr }) {
  const [d, setD] = useState(null);
  const [pix, setPix] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/boy/fees").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
  }, [load]);
  const pay = async (inv) => {
    setBusy(inv.id);
    try {
      setPix((await api.post(`/boy/fees/${inv.id}/pix`)).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  if (!d) return <p className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></p>;
  const how = !d.fee_pct ? "No momento não há taxa da plataforma sobre as corridas."
    : d.mode === "direto" ? `Com o Mercado Pago conectado em Boy Express → Configurações, a taxa é descontada direto em cada pagamento. Sem a conexão, ela entra na fatura ${PERIOD_LABELS[d.period]}.`
      : `A taxa é somada e cobrada em fatura ${PERIOD_LABELS[d.period]}, paga com Pix.`;
  return (
    <div className="space-y-4" data-testid="boy-fees-tab">
      <div className="card-surface p-5"><p className="eyebrow">Taxa da plataforma</p><p className="mt-1 text-2xl font-bold" data-testid="boy-fee-pct">{d.fee_pct}% por corrida paga</p><p className="text-sm text-muted-foreground">{how}</p></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Box title="Período atual" value={brl(d.current.amount)} hint={`${d.current.rides} corrida(s) · ${fmtDate(d.current.start)} a ${endDay(d.current.end)}`} testId="boy-fee-current" />
        <Box title="Descontado direto" value={brl(d.direct.amount)} hint={`${d.direct.rides} corrida(s) com desconto automático`} testId="boy-fee-direct" />
      </div>
      <div className="space-y-2">
        <p className="eyebrow">Faturas</p>
        {!d.invoices.length && <p className="card-surface p-5 text-center text-sm text-muted-foreground">Nenhuma fatura fechada ainda.</p>}
        {d.invoices.map((i) => (
          <div key={i.id} className="card-surface flex flex-wrap items-center gap-3 p-4" data-testid={`boy-invoice-${i.id}`}>
            <Receipt className="h-5 w-5 text-muted-foreground" />
            <div className="min-w-0 flex-1"><p className="font-semibold">{fmtDate(i.start)} a {endDay(i.end)}</p><p className="text-xs text-muted-foreground">{i.rides} corrida(s)</p></div>
            <span className="font-mono font-semibold">{brl(i.amount)}</span>
            <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", i.status === "paga" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-amber-500/15 text-amber-700 dark:text-amber-300")}>{i.status === "paga" ? "Paga" : "Em aberto"}</span>
            {i.status === "aberta" && mgr && <Button size="sm" onClick={() => pay(i)} disabled={busy === i.id} data-testid={`boy-invoice-pay-${i.id}`}>{busy === i.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <QrCode className="mr-1.5 h-4 w-4" />}Pagar com Pix</Button>}
          </div>
        ))}
      </div>
      <PixDialog inv={pix} onClose={() => setPix(null)} onPaid={() => { setPix(null); load(); }} />
    </div>
  );
}
